import { storage } from 'lib/storage';
import { STORAGE_KEYS } from 'lib/storageKeys';

export interface AuthConfig {
  name: string;
  apiBaseUrl: string;
  clientId: string;
  clientSecret: string;
}

export const saveAuthConfigs = (configs: AuthConfig[]): void => {
  storage.set(STORAGE_KEYS.authConfigs, configs);
};

export const loadAuthConfigs = (): AuthConfig[] => {
  const stored = storage.get<AuthConfig[]>(STORAGE_KEYS.authConfigs);
  return Array.isArray(stored) ? stored : [];
};

export const saveActiveConfigName = (name: string): void => {
  storage.set(STORAGE_KEYS.activeAuthConfig, name);
};

/**
 * Read the active-config name. Handles two formats:
 *   - JSON-wrapped (current): localStorage value is e.g. `"A"`
 *   - Legacy raw string (POC era): localStorage value is e.g. `A`
 * Legacy raw values are rewritten through `storage.set` on first read so
 * subsequent calls parse cleanly.
 */
export const loadActiveConfigName = (): string | null => {
  const raw = storage.getRaw(STORAGE_KEYS.activeAuthConfig);
  if (raw === null) return null;
  if (raw.startsWith('"')) {
    return storage.get<string>(STORAGE_KEYS.activeAuthConfig);
  }
  // legacy raw string — migrate forward, then return it
  storage.set(STORAGE_KEYS.activeAuthConfig, raw);
  return raw;
};

export const getActiveConfig = (): AuthConfig | null => {
  const configs = loadAuthConfigs();
  const activeName = loadActiveConfigName();
  if (!activeName) {
    return configs[0] ?? null;
  }

  return configs.find((c) => c.name === activeName) ?? configs[0] ?? null;
};

export const addAuthConfig = (config: AuthConfig): void => {
  const configs = loadAuthConfigs();
  const existingIndex = configs.findIndex((c) => c.name === config.name);
  if (existingIndex >= 0) {
    configs[existingIndex] = config;
  } else {
    configs.push(config);
  }
  saveAuthConfigs(configs);
};

export const removeAuthConfig = (name: string): void => {
  const configs = loadAuthConfigs();
  const filtered = configs.filter((c) => c.name !== name);
  saveAuthConfigs(filtered);
  if (loadActiveConfigName() === name) {
    saveActiveConfigName(filtered[0]?.name ?? '');
  }
};
